import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Collapse,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  OpenInNew as OpenInNewIcon,
  AutoAwesome as EnrichIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Block as SkipIcon,
  Undo as UndoIcon,
  EmojiEvents as AwardsIcon,
  Comment as NotesIcon,
  WarningAmber as AlertListIcon,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';

// Lead source chip metadata (SGPBusiness alert list integration)
const SOURCE_META = {
  gebiz: { label: 'GeBIZ', color: 'info' },
  sgpbusiness_alert: { label: 'SGP Alert', color: 'warning' },
};

// Skip reasons matching backend enum
const SKIP_REASONS = [
  { value: 'not_a_company', label: 'Not a Company', description: 'Entry is not a business entity' },
  { value: 'duplicate', label: 'Duplicate', description: 'Already exists in the system' },
  { value: 'invalid_data', label: 'Invalid Data', description: 'Data is incorrect or corrupted' },
  { value: 'out_of_scope', label: 'Out of Scope', description: 'Not relevant to this job' },
  { value: 'government_agency', label: 'Government Agency', description: 'Is a government entity' },
  { value: 'individual_person', label: 'Individual Person', description: 'Is a person, not a company' },
  { value: 'other', label: 'Other', description: 'Other reason' },
];

// Skip Reason Selection Dialog
const SkipReasonDialog = ({ open, onClose, onSelect, title = 'Select Skip Reason', loading = false }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <List sx={{ pt: 0 }}>
            {SKIP_REASONS.map((reason) => (
              <ListItem key={reason.value} disablePadding>
                <ListItemButton onClick={() => onSelect(reason.value)}>
                  <ListItemText 
                    primary={reason.label} 
                    secondary={reason.description}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

// Expandable Row Component
const LeadRow = ({ lead, onViewDetails, isSelected, onToggleSelect, onSkipClick, onUnskipClick, onViewAwards, onViewNotes, skipLoading }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      case 'skipped': return 'secondary';
      case 'pending': return 'default';
      case 'rerun': return 'info';
      default: return 'default';
    }
  };

  const getQualityColor = (score) => {
    if (score >= 80) return '#2e7d32'; // green
    if (score >= 60) return '#ed6c02'; // orange
    return '#d32f2f'; // red
  };

  const qualityScore = lead.data_quality_score || 0;
  const isSkipped = lead.status?.toLowerCase() === 'skipped';
  const isEnriched = lead.enrichment_status === 'enriched';

  // Extract website URL safely
  const getWebsite = () => {
    try {
      if (Array.isArray(lead.company_website) && lead.company_website[0]) {
        return lead.company_website[0].value || lead.company_website[0];
      }
      return lead.company_website || null;
    } catch {
      return null;
    }
  };

  // Extract business info safely
  const getBusinessInfo = () => {
    try {
      if (Array.isArray(lead.business_info) && lead.business_info[0]) {
        const info = lead.business_info[0].value || lead.business_info[0];
        return typeof info === 'string' ? info : String(info);
      }
      return typeof lead.business_info === 'string' ? lead.business_info : null;
    } catch {
      return null;
    }
  };

  const website = getWebsite();
  const businessInfo = getBusinessInfo();

  return (
    <>
      <TableRow 
        hover 
        sx={{ 
          '& > *': { borderBottom: expanded ? 'none' : undefined },
          cursor: 'pointer',
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect(lead.id);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </TableCell>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>
            #{lead.id}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ fontWeight: 600, cursor: 'pointer' }}
              onClick={() => onViewDetails(lead)}
            >
              {lead.company_name}
            </Typography>
            {isSkipped ? (
              <Chip
                icon={<SkipIcon sx={{ fontSize: 14 }} />}
                label="Skipped"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }}
              />
            ) : isEnriched ? (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label="Enriched"
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }}
              />
            ) : (
              <Chip
                icon={<PendingIcon sx={{ fontSize: 14, color: '#5D4037' }} />}
                label="Pending"
                size="small"
                variant="outlined"
                sx={{ 
                  height: 20, 
                  fontSize: '0.65rem', 
                  '& .MuiChip-icon': { ml: 0.5 },
                  color: '#5D4037',  // Dark brown text
                  borderColor: '#5D4037',  // Dark brown border
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {lead.source && SOURCE_META[lead.source] ? (
              <Chip
                label={SOURCE_META[lead.source].label}
                color={SOURCE_META[lead.source].color}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
            {lead.on_sgpbusiness_alert_list && (
              <Tooltip title="On SGPBusiness alert list">
                <AlertListIcon sx={{ fontSize: 16, color: '#ed6c02' }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={lead.status?.toUpperCase() || 'PENDING'}
            color={getStatusColor(lead.status)}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
            <LinearProgress
              variant="determinate"
              value={qualityScore}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: getQualityColor(qualityScore),
                }
              }}
            />
            <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 500 }}>
              {qualityScore}%
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {lead.fields_completed || 0}/{lead.total_fields || 0}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {lead.processed_at 
              ? new Date(lead.processed_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })
              : 'Pending'
            }
          </Typography>
        </TableCell>
        <TableCell>
          {/* Notes */}
          <Tooltip title={`${lead.note_count || 0} Note${(lead.note_count || 0) !== 1 ? 's' : ''}`}>
            <IconButton
              size="small"
              color={lead.note_count > 0 ? 'primary' : 'default'}
              onClick={(e) => {
                e.stopPropagation();
                onViewNotes && onViewNotes(lead);
              }}
            >
              <Badge 
                badgeContent={lead.note_count || 0} 
                color="primary" 
                max={99}
                invisible={!lead.note_count}
              >
                <NotesIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>
          {/* Actions - View Awards, Skip for PENDING, Unskip for SKIPPED */}
          <Tooltip title="View GeBIZ Awards">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onViewAwards && onViewAwards(lead);
              }}
            >
              <AwardsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {lead.status?.toUpperCase() === 'PENDING' && (
            <Tooltip title="Skip this lead">
              <IconButton
                size="small"
                color="warning"
                onClick={(e) => {
                  e.stopPropagation();
                  onSkipClick && onSkipClick(lead);
                }}
                disabled={skipLoading}
              >
                <SkipIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {lead.status?.toUpperCase() === 'SKIPPED' && (
            <Tooltip title={`Unskip (was: ${lead.skipped_reason || 'unknown'})`}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onUnskipClick && onUnskipClick(lead);
                }}
                disabled={skipLoading}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      
      {/* Expanded Row Content */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 3, backgroundColor: '#fafafa' }}>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {/* Website */}
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Website
                  </Typography>
                  {website ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#1976d2' }}>
                        {website}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => window.open(website.startsWith('http') ? website : `https://${website}`, '_blank')}
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </Box>

                {/* Business Info */}
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Business Info
                  </Typography>
                  <Typography variant="body2">
                    {businessInfo ? (businessInfo.length > 200 ? businessInfo.substring(0, 200) + '...' : businessInfo) : '—'}
                  </Typography>
                </Box>

                {/* Flags */}
                {lead.flags && lead.flags.length > 0 && (
                  <Box sx={{ minWidth: 150 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Flags
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {lead.flags.map(flag => (
                        <Chip
                          key={flag.id}
                          label={flag.name}
                          size="small"
                          sx={{
                            backgroundColor: flag.color,
                            color: '#fff',
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* View Details Button */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onViewDetails(lead)}
                  >
                    View Full Details
                  </Button>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const LeadsTable = ({ 
  leads = [], 
  totalCount = 0,
  onViewDetails,
  onViewAwards,
  onViewNotes,
  onExportCSV,
  onExportExcel,
  onCustomExport,
  customExportLoading = false,
  onEnrichSelected,
  enrichLoading = false,
  loading = false,
  selectedLeads = [],
  onToggleSelect,
  onSelectAll,
  // Pagination props
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  // Filter props (controlled from parent - server-side filtering)
  searchQuery = '',
  onSearchChange,
  statusFilter = 'all',
  onStatusFilterChange,
  enrichmentFilter = 'all',
  onEnrichmentFilterChange,
  sourceFilter = 'all',
  onSourceFilterChange,
  // Skip/Unskip props
  onSkipLead,
  onUnskipLead,
  onSkipSelectedLeads,
  onUnskipSelectedLeads,
  skipLoading = false,
}) => {
  // State for skip reason dialog
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipTarget, setSkipTarget] = useState(null); // { type: 'single' | 'bulk', lead?: object }
  
  // No client-side filtering - server handles it
  // leads prop already contains filtered results

  const isAllSelected = leads.length > 0 && 
    leads.every(lead => selectedLeads.includes(lead.id));
  
  const isIndeterminate = selectedLeads.length > 0 && 
    selectedLeads.length < leads.length;
  
  // Check if all selected leads are PENDING (for bulk skip)
  const selectedLeadsData = leads.filter(l => selectedLeads.includes(l.id));
  const allSelectedArePending = selectedLeadsData.length > 0 && 
    selectedLeadsData.every(l => l.status?.toUpperCase() === 'PENDING');
  
  // Check if all selected leads are SKIPPED (for bulk unskip)
  const allSelectedAreSkipped = selectedLeadsData.length > 0 && 
    selectedLeadsData.every(l => l.status?.toUpperCase() === 'SKIPPED');
  
  // Handle skip button click (opens dialog)
  const handleSkipClick = (lead) => {
    setSkipTarget({ type: 'single', lead });
    setSkipDialogOpen(true);
  };
  
  // Handle bulk skip button click (opens dialog)
  const handleBulkSkipClick = () => {
    setSkipTarget({ type: 'bulk' });
    setSkipDialogOpen(true);
  };
  
  // Handle reason selection from dialog
  const handleReasonSelect = async (reason) => {
    if (skipTarget?.type === 'single' && skipTarget.lead) {
      // Single lead skip
      if (onSkipLead) {
        await onSkipLead(skipTarget.lead.id, reason);
      }
    } else if (skipTarget?.type === 'bulk') {
      // Bulk skip
      if (onSkipSelectedLeads) {
        await onSkipSelectedLeads(selectedLeads, reason);
      }
    }
    setSkipDialogOpen(false);
    setSkipTarget(null);
  };
  
  // Handle unskip click (no dialog needed)
  const handleUnskipClick = async (lead) => {
    if (onUnskipLead) {
      await onUnskipLead(lead.id);
    }
  };
  
  // Handle bulk unskip
  const handleBulkUnskipClick = async () => {
    if (onUnskipSelectedLeads) {
      await onUnskipSelectedLeads(selectedLeads);
    }
  };

  return (
    <Box>
      {/* Header with Search, Filter, and Export */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search */}
        <TextField
          placeholder="Search by company name or ID..."
          value={searchQuery}
          onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange && onStatusFilterChange(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="PROCESSING">Processing</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="SKIPPED">Skipped</MenuItem>
            <MenuItem value="FAILED">Failed</MenuItem>
            <MenuItem value="RERUN">Rerun</MenuItem>
          </Select>
        </FormControl>

        {/* Enrichment Filter */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Enrichment</InputLabel>
          <Select
            value={enrichmentFilter}
            onChange={(e) => onEnrichmentFilterChange && onEnrichmentFilterChange(e.target.value)}
            label="Enrichment"
          >
            <MenuItem value="all">All Leads</MenuItem>
            <MenuItem value="enriched">Enriched Only</MenuItem>
            <MenuItem value="pending_enrichment">Pending Enrichment</MenuItem>
          </Select>
        </FormControl>

        {/* Source Filter */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Source</InputLabel>
          <Select
            value={sourceFilter}
            onChange={(e) => onSourceFilterChange && onSourceFilterChange(e.target.value)}
            label="Source"
          >
            <MenuItem value="all">All Sources</MenuItem>
            <MenuItem value="gebiz">GeBIZ</MenuItem>
            <MenuItem value="sgpbusiness_alert">SGPBusiness Alert</MenuItem>
          </Select>
        </FormControl>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Selected count */}
        {selectedLeads.length > 0 && (
          <Chip 
            label={`${selectedLeads.length} selected`}
            color="primary"
            size="small"
            sx={{ mr: 1 }}
          />
        )}

        {/* Bulk Skip Button - Show when all selected are PENDING */}
        {selectedLeads.length > 0 && allSelectedArePending && onSkipSelectedLeads && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={skipLoading ? <CircularProgress size={16} /> : <SkipIcon />}
            onClick={handleBulkSkipClick}
            disabled={skipLoading}
            size="small"
          >
            Skip ({selectedLeads.length})
          </Button>
        )}
        
        {/* Bulk Unskip Button - Show when all selected are SKIPPED */}
        {selectedLeads.length > 0 && allSelectedAreSkipped && onUnskipSelectedLeads && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={skipLoading ? <CircularProgress size={16} /> : <UndoIcon />}
            onClick={handleBulkUnskipClick}
            disabled={skipLoading}
            size="small"
          >
            Unskip ({selectedLeads.length})
          </Button>
        )}

        {/* Enrich Button */}
        {onEnrichSelected && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={enrichLoading ? null : <EnrichIcon />}
            onClick={onEnrichSelected}
            disabled={selectedLeads.length === 0 || enrichLoading}
          >
            {enrichLoading ? 'Starting...' : `Enrich${selectedLeads.length > 0 ? ` (${selectedLeads.length})` : ''}`}
          </Button>
        )}

        {/* Export Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={onExportCSV}
            disabled={leads.length === 0}
          >
            CSV
          </Button>
          {onExportExcel && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={onExportExcel}
              disabled={leads.length === 0}
            >
              Excel
            </Button>
          )}
          {onCustomExport && (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={onCustomExport}
              disabled={leads.length === 0 || customExportLoading}
            >
              {customExportLoading ? 'Loading...' : 'Custom Export'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => onSelectAll && onSelectAll(e.target.checked, leads.map(l => l.id))}
                />
              </TableCell>
              <TableCell sx={{ width: 50 }}></TableCell>
              <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Data Quality</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Fields</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Processed</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 70 }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Loading leads...</Typography>
                </TableCell>
              </TableRow>
            ) : leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchQuery || statusFilter !== 'all' || enrichmentFilter !== 'all' || sourceFilter !== 'all'
                      ? 'No leads match your search criteria'
                      : 'No leads found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              leads.map((lead) => (
                <LeadRow
                  key={lead.id}
                  lead={lead}
                  onViewDetails={onViewDetails}
                  onViewAwards={onViewAwards}
                  onViewNotes={onViewNotes}
                  isSelected={selectedLeads.includes(lead.id)}
                  onToggleSelect={onToggleSelect}
                  onSkipClick={handleSkipClick}
                  onUnskipClick={handleUnskipClick}
                  skipLoading={skipLoading}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        {selectedLeads.length > 0 && (
          <Typography variant="body2" color="primary" sx={{ pl: 2 }}>
            {selectedLeads.length} selected
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Leads per page:"
          showFirstButton
          showLastButton
        />
      </Box>
      
      {/* Skip Reason Dialog */}
      <SkipReasonDialog
        open={skipDialogOpen}
        onClose={() => {
          setSkipDialogOpen(false);
          setSkipTarget(null);
        }}
        onSelect={handleReasonSelect}
        title={skipTarget?.type === 'bulk' 
          ? `Skip ${selectedLeads.length} Lead${selectedLeads.length > 1 ? 's' : ''}`
          : `Skip Lead: ${skipTarget?.lead?.company_name || ''}`
        }
        loading={skipLoading}
      />
    </Box>
  );
};

export default LeadsTable;

